"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { Copy, Download } from "lucide-react";
import { NextIntlClientProvider } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { parse, stringify } from "subtitletools";
import * as z from "zod";
import { addMessage } from "@/components/Messages";
import { Button } from "@/components/ui/button";
import * as RadixForm from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SubtitlesForm } from "@/lib/subtitletools/forms";
import { ResultDialog } from "@/lib/subtitletools/ResultDialog";
import { addRecentSubtitle, recentSubtitlesAtom } from "@/lib/subtitletools/recentSubtitles";
import enMessages from "@/messages/en.json";

const supportedFormats = ["srt", "ass", "ssa", "smi", "sub", "vtt"];

const formSchema = z.object({
  baseSubtitleText: z.string().min(1),
  mergeSubtitleText: z.string().min(1),
  mode: z.enum(["nearest", "simple", "glue"]),
  threshold: z.number().int().min(0).default(1000),
  topOfScreen: z.boolean().default(false),
  removeBreaksBase: z.boolean().default(true),
  removeBreaksMerge: z.boolean().default(true),
  colorMerge: z.boolean().default(false),
  colorBase: z.boolean().default(false),
});

type FormType = z.infer<typeof formSchema>;

const MergeSubtitlesContent = () => {
  const [mergedResult, setMergedResult] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [recentSubtitles, setRecentSubtitles] = useAtom(recentSubtitlesAtom);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      baseSubtitleText: "",
      mergeSubtitleText: "",
      mode: "nearest",
      threshold: 1000,
      topOfScreen: false,
      removeBreaksBase: true,
      removeBreaksMerge: true,
      colorMerge: false,
      colorBase: false,
    },
  });

  // Merge logic
  const handleMerge = async (data: FormType) => {
    setLoading(true);
    setError(null);
    setMergedResult("");
    try {
      if (!data.baseSubtitleText.trim() || !data.mergeSubtitleText.trim()) {
        setError("Both subtitles are required.");
        setLoading(false);
        return;
      }
      // Only SRT/VTT supported for now
      let baseCaptions: ReturnType<typeof parse>;
      let mergeCaptions: ReturnType<typeof parse>;
      try {
        baseCaptions = parse(data.baseSubtitleText);
        mergeCaptions = parse(data.mergeSubtitleText);
      } catch (_e) {
        setError("Failed to parse one of the files. Only SRT/VTT supported in this demo.");
        setLoading(false);
        return;
      }
      // Remove line breaks if needed
      if (data.removeBreaksBase)
        baseCaptions = baseCaptions.map((c) => ({
          ...c,
          text: c.text.replace(/\n/g, " "),
        }));
      if (data.removeBreaksMerge)
        mergeCaptions = mergeCaptions.map((c) => ({
          ...c,
          text: c.text.replace(/\n/g, " "),
        }));
      // Merge logic
      let mergedCaptions: any[] = [];
      if (data.mode === "nearest") {
        mergedCaptions = baseCaptions.map((base) => {
          let nearest = null;
          let minDist = Infinity;
          for (const merge of mergeCaptions) {
            const dist = Math.abs(base.start - merge.start);
            if (dist < minDist && dist <= data.threshold) {
              minDist = dist;
              nearest = merge;
            }
          }
          let text = base.text;
          if (nearest) {
            text += "\n" + (data.topOfScreen ? "{an8}" : "") + nearest.text;
          }
          return {
            ...base,
            text,
          };
        });
      } else if (data.mode === "simple") {
        mergedCaptions = baseCaptions.map((base, i) => {
          const merge = mergeCaptions[i];
          let text = base.text;
          if (merge) {
            text += "\n" + (data.topOfScreen ? "{an8}" : "") + merge.text;
          }
          return {
            ...base,
            text,
          };
        });
      } else if (data.mode === "glue") {
        mergedCaptions = [...baseCaptions, ...mergeCaptions];
      }
      // Coloring
      if (data.colorMerge || data.colorBase) {
        mergedCaptions = mergedCaptions.map((cue, _i) => {
          let [base, merge] = cue.text.split("\n");
          if (data.colorBase && base) base = `<font color="cyan">${base}</font>`;
          if (data.colorMerge && merge) merge = `<font color="yellow">${merge}</font>`;
          return {
            ...cue,
            text: [base, merge].filter(Boolean).join("\n"),
          };
        });
      }
      // Stringify
      const mergedSrt = stringify(mergedCaptions, { format: "srt" });
      setMergedResult(mergedSrt);
      setShowResultDialog(true);

      // Save merged subtitle to recent subtitles
      try {
        const duration =
          mergedCaptions.length > 0
            ? Math.max(...mergedCaptions.map((c) => c.end)) -
              Math.min(...mergedCaptions.map((c) => c.start))
            : 0;

        setRecentSubtitles(
          addRecentSubtitle(
            mergedSrt,
            `Merged subtitles (${mergedCaptions.length} captions, ${data.mode} mode)`,
            mergedCaptions.length,
            duration
          )
        );
      } catch (error) {
        console.warn("Failed to save merged subtitle:", error);
      }
    } catch (e: any) {
      setError(e.message || "Unknown error");
    }
    setLoading(false);
  };

  const copyToClipboard = () => {
    if (navigator.clipboard && mergedResult) {
      navigator.clipboard.writeText(mergedResult);
      addMessage({ text: "Copied", type: "SUCCESS" });
    }
  };
  const downloadFile = () => {
    const blob = new Blob([mergedResult], { type: "text/srt" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "merged.srt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <NextIntlClientProvider messages={enMessages} locale={"en"} timeZone="Europe/Oslo">
      <div className="container max-w-4xl mx-auto py-10">
        <h1 className="text-3xl font-bold mb-6">Merge Subtitles</h1>

        <RadixForm.Form {...form}>
          <form onSubmit={form.handleSubmit(handleMerge)} className="space-y-6" autoComplete="off">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <RadixForm.FormLabel className="text-base font-semibold">
                    Base subtitle
                  </RadixForm.FormLabel>
                  <SubtitlesForm
                    setInputSrt={(newValue) => form.setValue("baseSubtitleText", newValue)}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <RadixForm.FormLabel className="text-base font-semibold">
                    Merge subtitle
                  </RadixForm.FormLabel>
                  <SubtitlesForm
                    setInputSrt={(newValue) => form.setValue("mergeSubtitleText", newValue)}
                  />
                </div>
              </div>
            </div>

            <div>
              <RadixForm.FormField
                control={form.control}
                name="mode"
                render={({ field }) => (
                  <RadixForm.FormItem className="space-y-3">
                    <RadixForm.FormLabel>Mode</RadixForm.FormLabel>
                    <RadixForm.FormControl>
                      <div className="space-y-1">
                        <label className="flex items-center gap-2">
                          <input type="radio" {...field} value="nearest" /> Nearest cue
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="radio" {...field} value="simple" /> Simple
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="radio" {...field} value="glue" /> Glue end-to-end (CD1 + CD2)
                        </label>
                      </div>
                    </RadixForm.FormControl>
                    <RadixForm.FormMessage />
                  </RadixForm.FormItem>
                )}
              />
            </div>

            <div>
              <RadixForm.FormLabel className="block mb-2">Options</RadixForm.FormLabel>
              <div className="flex items-center gap-2 mt-1">
                <label className="text-sm">Cue combining threshold in milliseconds</label>
                <RadixForm.FormField
                  control={form.control}
                  name="threshold"
                  render={({ field }) => (
                    <RadixForm.FormItem>
                      <RadixForm.FormControl>
                        <Input
                          type="number"
                          min={0}
                          {...field}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                          className="w-24"
                        />
                      </RadixForm.FormControl>
                      <RadixForm.FormMessage />
                    </RadixForm.FormItem>
                  )}
                />
                <span className="text-xs text-gray-500">(1000 ms = 1 second)</span>
              </div>
              <div className="space-y-1 mt-2">
                <RadixForm.FormField
                  control={form.control}
                  name="topOfScreen"
                  render={({ field }) => (
                    <RadixForm.FormItem>
                      <RadixForm.FormControl>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={field.value} onChange={field.onChange} />{" "}
                          Put the &ldquo;merge&rdquo; subtitle at the top of the screen
                        </label>
                      </RadixForm.FormControl>
                      <RadixForm.FormMessage />
                    </RadixForm.FormItem>
                  )}
                />
                <RadixForm.FormField
                  control={form.control}
                  name="removeBreaksBase"
                  render={({ field }) => (
                    <RadixForm.FormItem>
                      <RadixForm.FormControl>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={field.value} onChange={field.onChange} />{" "}
                          Remove line breaks from the base subtitle
                        </label>
                      </RadixForm.FormControl>
                      <RadixForm.FormMessage />
                    </RadixForm.FormItem>
                  )}
                />
                <RadixForm.FormField
                  control={form.control}
                  name="removeBreaksMerge"
                  render={({ field }) => (
                    <RadixForm.FormItem>
                      <RadixForm.FormControl>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={field.value} onChange={field.onChange} />{" "}
                          Remove line breaks from the merge subtitle
                        </label>
                      </RadixForm.FormControl>
                      <RadixForm.FormMessage />
                    </RadixForm.FormItem>
                  )}
                />
              </div>
            </div>

            <div>
              <RadixForm.FormLabel className="block mb-2">Styling</RadixForm.FormLabel>
              <div className="space-y-1 mt-2">
                <RadixForm.FormField
                  control={form.control}
                  name="colorMerge"
                  render={({ field }) => (
                    <RadixForm.FormItem>
                      <RadixForm.FormControl>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={field.value} onChange={field.onChange} />{" "}
                          Color the &ldquo;merge&rdquo; subtitle file
                        </label>
                      </RadixForm.FormControl>
                      <RadixForm.FormMessage />
                    </RadixForm.FormItem>
                  )}
                />
                <RadixForm.FormField
                  control={form.control}
                  name="colorBase"
                  render={({ field }) => (
                    <RadixForm.FormItem>
                      <RadixForm.FormControl>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={field.value} onChange={field.onChange} />{" "}
                          Color the &ldquo;base&rdquo; subtitle file
                        </label>
                      </RadixForm.FormControl>
                      <RadixForm.FormMessage />
                    </RadixForm.FormItem>
                  )}
                />
              </div>
            </div>

            {error && <div className="text-red-600 text-sm">{error}</div>}

            <Button type="submit" disabled={loading} className="w-32">
              {loading ? "Merging..." : "Merge"}
            </Button>
          </form>
        </RadixForm.Form>

        {mergedResult && (
          <div className="mt-8">
            <div className="flex gap-2 mb-2">
              <Button type="button" variant="outline" onClick={copyToClipboard}>
                <Copy className="h-4 w-4 mr-1" /> Copy
              </Button>
              <Button type="button" variant="outline" onClick={downloadFile}>
                <Download className="h-4 w-4 mr-1" /> Download
              </Button>
            </div>
            <Textarea value={mergedResult} rows={12} readOnly className="font-mono text-sm" />
          </div>
        )}

        {/* Result Dialog */}
        <ResultDialog
          open={showResultDialog}
          onOpenChange={setShowResultDialog}
          title="Merge Complete"
          description="Successfully merged subtitles"
          content={mergedResult}
          fileName="merged.srt"
        />
      </div>
    </NextIntlClientProvider>
  );
};

export default MergeSubtitlesContent;
